/**
 * Business Logic Layer - Fine-Tuning Service
 * Contains business rules and orchestrates data operations for fine-tuning
 */

import {
  FineTuningRepository,
  UploadDatasetResponse,
  ListTrainingFilesResponse,
  TrainingFile,
  TrainingJob,
  ListTrainingJobsResponse,
  StartTrainingJobRequest,
  StartTrainingJobResponse,
  DeployFineTunedModelRequest,
  DeployFineTunedModelResponse,
  CreateModelConfigRequest,
  CreateModelConfigResponse,
} from '../repositories/FineTuningRepository';

export class FineTuningService {
  private repository: FineTuningRepository;

  constructor() {
    this.repository = FineTuningRepository.getInstance();
  }

  /**
   * Upload dataset for fine-tuning
   * Handles validation and business logic before calling repository
   */
  public async uploadDataset(
    file: File,
    instanceId: string
  ): Promise<UploadDatasetResponse> {
    // Create FormData
    const formData = new FormData();
    formData.append('file', file);
    formData.append('instance_id', instanceId);
    formData.append('destination_path', 'training_data');

    // Call repository to upload
    return await this.repository.uploadDataset(formData);
  }

  /**
   * List training files for a specific instance
   */
  public async listTrainingFiles(instanceId: string): Promise<TrainingFile[]> {
    const response = await this.repository.listTrainingFiles(instanceId);
    // Return only the files array
    return response.files;
  }

  /**
   * Get training jobs for a namespace
   */
  public async getTrainingJobs(namespace: string = 'dev'): Promise<TrainingJob[]> {
    const response = await this.repository.getTrainingJobs(namespace);
    // Return only the jobs array
    return response.jobs;
  }

  /**
   * Start a new training job
   */
  public async startTrainingJob(request: StartTrainingJobRequest): Promise<StartTrainingJobResponse> {
    // Call repository to start training job
    return await this.repository.startTrainingJob(request);
  }

  /**
   * Deploy a fine-tuned model
   */
  public async deployFineTunedModel(request: DeployFineTunedModelRequest): Promise<DeployFineTunedModelResponse> {
    // Call repository to deploy fine-tuned model
    return await this.repository.deployFineTunedModel(request);
  }

  /**
   * Create model configuration from fine-tuned model
   */
  public async createModelConfig(request: CreateModelConfigRequest): Promise<CreateModelConfigResponse> {
    // Call repository to create model configuration
    return await this.repository.createModelConfig(request);
  }
}
